import { useEffect, useState } from "react";
import { notFound } from "./NotFoundPage.jsx";
import { head } from "../components/common/PageHeader.jsx";
import { badge } from "../components/common/StatusBadge.jsx";
import { nodes } from "../components/common/reactHelpers.jsx";
import { esc, money } from "../utils/format.js";
import {
  select,
  field,
  errorSlot,
  btn,
} from "../components/forms/FormControls.jsx";
import { tableViews } from "../components/tables/RecordTable.jsx";
import { transitions } from "../utils/domain/model.js";

const ORDERS_API = "https://api.praachinorganics.com/api/admin/orders";

function orderDetail(
  oid,
  o,
  statuses,
  loading,
  error,
  onStatusUpdate,
  updating,
  updateError,
) {
  if (loading) {
    return <p className="hint">Loading order details...</p>;
  }

  if (!o) {
    return notFound();
  }

  const c = o.customer ?? {};
  const items = Array.isArray(o.items) ? o.items : [];

  // Preserve the existing transition rules and use backend status values.
  const allowedNames = Array.isArray(o.allowedNextStatuses)
    ? o.allowedNextStatuses
    : [];

  const currentTransitions = transitions[o.status] ?? [];

  const nextStatuses = statuses
    .filter((s) => {
      const allowedByApi = allowedNames.includes(s.Name);
      const allowedByExistingRules =
        currentTransitions.length === 0 || currentTransitions.includes(s.Name);

      return allowedByApi && allowedByExistingRules && s.Name !== o.status;
    })
    .map((s) => [String(s.Value), s.Name]);

  const canUpdate = nextStatuses.length > 0;

  return (
    <>
      {head("ORDER DETAIL", o.id, o.orderDate)}

      <div className="grid-detail">
        <section className="card">
          <div className="card-head">
            <h2>Line items</h2>
            {badge(o.fulfillmentStatus || o.status || "Unfulfilled")}
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Item / SKU snapshot</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Total</th>
                </tr>
              </thead>

              <tbody>
                {items.length > 0 ? (
                  nodes(
                    items.map((i) => (
                      <tr key={i.id}>
                        <td>
                          <b>{esc(i.productName || "")}</b>
                          <small>
                            {esc(i.skuCode || "")}
                            {" \u00B7 "}
                            {esc(i.variantName || "")}
                          </small>
                        </td>

                        <td>{esc(i.variantName || "")}</td>

                        <td>{i.quantity ?? ""}</td>

                        <td>{money(i.unitPrice ?? 0)}</td>

                        <td>{money(i.totalPrice ?? 0)}</td>
                      </tr>
                    )),
                  )
                ) : (
                  <tr>
                    <td colSpan={5} className="empty">
                      No line items available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="total-row">
            Merchandise <b>{money(o.merchandiseTotal ?? 0)}</b>
          </div>

          <div className="total-row">
            Discount {esc(o.couponCode || "")}{" "}
            <b>
              {"\u2212 "}
              {money(o.discountAmount ?? 0)}
            </b>
          </div>

          <div className="total-row">
            Shipping <b>{money(o.shippingAmount ?? 0)}</b>
          </div>

          <div className="total-row grand">
            Total <b>{money(o.grandTotal ?? 0)}</b>
          </div>

          <h2 className="space-top">Fulfilment</h2>

          {o.courier || o.trackingNumber ? (
            <p>
              {esc(o.courier || "")}
              {o.trackingNumber ? (
                <>
                  {" \u00B7 Tracking "}
                  {esc(o.trackingNumber)}
                </>
              ) : null}
            </p>
          ) : null}

          {canUpdate ? (
            <form id="order-status-form" onSubmit={onStatusUpdate}>
              {select("Next status", "status", "", [
                ["", "Select next status…"],
                ...nextStatuses,
              ])}

              <div className="form-grid">
                {field(
                  "Courier — required for dispatch",
                  "carrier",
                  o.courier || "",
                )}

                {field(
                  "Tracking number — required for dispatch",
                  "tracking",
                  o.trackingNumber || "",
                )}
              </div>

              <button className="btn" type="submit" disabled={updating}>
                {updating ? "Updating..." : "Update status"}
              </button>

              {errorSlot()}

              {updateError ? <p className="hint">{updateError}</p> : null}
            </form>
          ) : (
            <p className="hint">
              This order has no further status transitions available.
            </p>
          )}

          {error ? <p className="hint">{error}</p> : null}

          <p className="hint">
            Successful payment and tracking are required before dispatch.
            Cancellation is available only before dispatch.
          </p>
        </section>

        <div className="stack">
          <section className="card">
            <h2>Customer</h2>

            <p>
              <b>{esc(c.name || "Historical customer")}</b>
              <br />
              {esc(c.email || "")}
              <br />
              {esc(c.phone || "")}
              <br />
              {esc(c.addressLine1 || "")}
              {c.addressLine2 ? (
                <>
                  <br />
                  {esc(c.addressLine2)}
                </>
              ) : null}
              <br />
              {[c.city, c.state, c.postalCode, c.country]
                .filter(Boolean)
                .map(esc)
                .join(", ")}
            </p>

            {btn(
              "View customer",
              `data-nav="customer" data-id="${esc(c.userId ?? "")}"`,
              true,
            )}

            <div className="divider"></div>

            <h2>Payment</h2>

            <p>
              {badge("Not available")}
              {" \u00B7 "}
              {money(o.grandTotal ?? 0)}
            </p>

            {btn("View transaction", `data-nav="payment" data-id=""`, true)}
          </section>

          <section className="card">
            <h2>Order context</h2>

            <p className="hint">No support note.</p>

            <p className="hint">
              Stock reservation information is not available in the order
              details response.
            </p>

            <p className="hint">
              Line items, prices, discounts and shipping are saved snapshots.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}

export default function OrderDetailsPage({ recordId } = {}) {
  const [orderData, setOrderData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState("");

  async function fetchOrder() {
    if (recordId == null || recordId === "") {
      setOrderData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${ORDERS_API}/${encodeURIComponent(recordId)}`,
      );

      if (response.status === 404) {
        setOrderData(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`Unable to load order (${response.status}).`);
      }

      const result = await response.json();
      setOrderData(result);
    } catch (err) {
      setOrderData(null);
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchStatuses() {
    try {
      const response = await fetch(`${ORDERS_API}/order-statuses`);

      if (!response.ok) {
        throw new Error("Unable to load order statuses.");
      }

      const result = await response.json();
      setStatuses(Array.isArray(result) ? result : []);
    } catch {
      setStatuses([]);
    }
  }

  useEffect(() => {
    fetchOrder();
    fetchStatuses();
  }, [recordId]);

  async function handleStatusUpdate(event) {
    event.preventDefault();

    if (!orderData?.id || updating) {
      return;
    }

    setUpdateError("");

    const formData = new FormData(event.currentTarget);
    const selectedStatus = formData.get("status");

    if (selectedStatus === null || selectedStatus === "") {
      setUpdateError("Please select a next status.");
      return;
    }

    const nextStatus = statuses.find(
      (s) => String(s.Value) === String(selectedStatus),
    );

    if (!nextStatus) {
      setUpdateError("The selected order status is invalid.");
      return;
    }

    const courier = String(formData.get("carrier") ?? "").trim();
    const trackingNumber = String(formData.get("tracking") ?? "").trim();

    if (nextStatus.Name === "Shipped") {
      if (!courier) {
        setUpdateError("Courier is required for dispatch.");
        return;
      }

      if (!trackingNumber) {
        setUpdateError("Tracking number is required for dispatch.");
        return;
      }
    }

    setUpdating(true);

    try {
      const response = await fetch(`${ORDERS_API}/${orderData.id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nextStatus: nextStatus.Value,
          courier,
          trackingNumber,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.message ||
          `Unable to update order status (${response.status}).`,
        );
      }

      await fetchOrder();
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "Unable to update order status.",
      );
    } finally {
      setUpdating(false);
    }
  }

  return tableViews(
    orderDetail(
      recordId,
      orderData,
      statuses,
      loading,
      error,
      handleStatusUpdate,
      updating,
      updateError,
    ),
  );
}
